import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  TierPricing,
  ModulePricing,
  RealEstateKey,
  RealEstatePricingModelMap,
  CategoryMultipliers,
  REAL_ESTATE_LABELS,
  EMPTY_UNITS,
  effectiveCategoryPrice,
} from "../types";

interface PricingCalculatorProps {
  tierPricing: TierPricing;
  modulePricing: ModulePricing;
  rePricingModel: RealEstatePricingModelMap;
  categoryMultipliers: CategoryMultipliers;
}

const fmt = (n: number) => "\u20AC" + Math.round(n).toLocaleString("nl-BE");
const fmtD = (n: number, dec: number) =>
  "\u20AC" + n.toFixed(dec).replace(".", ",");
const reKeys = Object.keys(REAL_ESTATE_LABELS) as RealEstateKey[];

export const PricingCalculator = ({
  tierPricing,
  modulePricing,
  rePricingModel,
  categoryMultipliers,
}: PricingCalculatorProps) => {
  const tiers = Object.keys(tierPricing);
  const [quantities, setQuantities] = useState<Record<RealEstateKey, number>>({
    ...EMPTY_UNITS,
  });

  const updateQty = (key: RealEstateKey, value: number) => {
    setQuantities((prev) => ({ ...prev, [key]: value }));
  };

  const hasInput = Object.values(quantities).some((v) => v > 0);

  // Calculate per-tier pricing based on user input
  const tierCalc = useMemo(() => {
    // Calculate unit vs m² totals for free tier check
    const totalUnits = reKeys
      .filter((k) => rePricingModel[k] === "unit")
      .reduce((s, k) => s + (quantities[k] || 0), 0);
    const totalM2 = reKeys
      .filter((k) => rePricingModel[k] === "m2")
      .reduce((s, k) => s + (quantities[k] || 0), 0);

    return tiers.map((tier) => {
      const config = tierPricing[tier];
      if (!config) return { tier, config: {} as any, volumeCost: 0, totalMRR: 0, isFree: false, withinLimits: false, freeMaxUnits: 0, freeMaxM2: 0 };

      // Free tier: €0 if within limits
      if (config.isFree) {
        const maxUnits = config.freeMaxUnits ?? 3;
        const maxM2 = config.freeMaxM2 ?? 500;
        const withinLimits = totalUnits <= maxUnits && totalM2 <= maxM2;
        return {
          tier,
          config,
          volumeCost: 0,
          totalMRR: 0,
          isFree: true,
          withinLimits,
          freeMaxUnits: maxUnits,
          freeMaxM2: maxM2,
        };
      }

      let volumeCost = 0;
      for (const reKey of reKeys) {
        const qty = quantities[reKey];
        if (qty <= 0) continue;
        const catPrice = effectiveCategoryPrice(
          reKey,
          config,
          modulePricing,
          rePricingModel,
          categoryMultipliers
        );
        volumeCost += catPrice * qty;
      }
      const totalMRR = config.basePrice + volumeCost;
      return { tier, config, volumeCost, totalMRR, isFree: false, withinLimits: false, freeMaxUnits: 0, freeMaxM2: 0 };
    });
  }, [quantities, tierPricing, modulePricing, rePricingModel]);

  return (
    <div className="bg-white rounded-xl border border-blue-200 p-5 mt-6 mx-6 mb-6">
      <h3 className="font-bold text-sm mb-1">
        Bereken uw prijs
      </h3>
      <p className="text-[11px] text-gray-500 mb-4">
        Vul uw vastgoedportfolio in en zie direct de maandelijkse prijs per tier.
      </p>

      {/* Input grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {reKeys.map((key) => {
          const info = REAL_ESTATE_LABELS[key];
          const isM2 = rePricingModel[key] === "m2";
          return (
            <div key={key} className="space-y-1">
              <label className="text-[10px] text-gray-500 flex items-center gap-1">
                <span>{info.icon}</span>
                {info.label}
                <span className="text-[8px] text-gray-400">
                  ({isM2 ? "m\u00B2" : "units"})
                </span>
              </label>
              <Input
                type="number"
                min="0"
                value={quantities[key] || ""}
                onChange={(e) =>
                  updateQty(key, parseInt(e.target.value) || 0)
                }
                placeholder="0"
                className="h-8 text-xs"
              />
            </div>
          );
        })}
      </div>

      {/* Results per tier */}
      {hasInput && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {tierCalc.map((tc) => {
            const { tier, config, totalMRR, isFree: isFreeCalc, withinLimits, freeMaxUnits, freeMaxM2 } = tc;
            if (!config || !config.label) return null;
            const isPopular = !!config.isPopular;

            return (
              <Card
                key={tier}
                className={`transition-shadow ${
                  isPopular
                    ? "border-blue-600 ring-2 ring-blue-600/10"
                    : ""
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{config.icon}</span>
                    <span className="font-bold text-sm">{config.label}</span>
                    {isPopular && (
                      <span className="text-[8px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-semibold">
                        Populair
                      </span>
                    )}
                  </div>

                  {isFreeCalc ? (
                    <>
                      <p
                        className={`text-2xl font-bold tracking-tight ${withinLimits ? "text-emerald-600" : "text-gray-400"}`}
                        style={{ fontFamily: "'DM Mono', monospace" }}
                      >
                        {withinLimits ? "\u20AC0" : "N/A"}
                        <span className="text-xs font-normal text-gray-500"> /mnd</span>
                      </p>
                      <p className="text-[10px] text-gray-500 mt-1">
                        {withinLimits
                          ? `Gratis tot ${freeMaxUnits} units en ${freeMaxM2.toLocaleString("nl-BE")} m\u00B2`
                          : `Overschrijdt gratis limiet (max ${freeMaxUnits} units / ${freeMaxM2.toLocaleString("nl-BE")} m\u00B2)`}
                      </p>
                    </>
                  ) : (
                    <>
                      <p
                        className="text-2xl font-bold text-blue-600 tracking-tight"
                        style={{ fontFamily: "'DM Mono', monospace" }}
                      >
                        {fmt(totalMRR)}
                        <span className="text-xs font-normal text-gray-500"> /mnd</span>
                      </p>

                      <div className="mt-2 space-y-0.5">
                        <div className="flex justify-between text-[10px] text-gray-500">
                          <span>Basisprijs</span>
                          <span>{fmt(config.basePrice)}</span>
                        </div>
                        {reKeys
                          .filter((k) => quantities[k] > 0)
                          .map((k) => {
                            const catPrice = effectiveCategoryPrice(
                              k,
                              config,
                              modulePricing,
                              rePricingModel,
                              categoryMultipliers
                            );
                            const isM2 = rePricingModel[k] === "m2";
                            return (
                              <div
                                key={k}
                                className="flex justify-between text-[10px] text-gray-500"
                              >
                                <span>
                                  {REAL_ESTATE_LABELS[k].icon}{" "}
                                  {quantities[k].toLocaleString("nl-BE")}{" "}
                                  {isM2 ? "m\u00B2" : "units"} &times;{" "}
                                  {fmtD(catPrice, isM2 ? 3 : 2)}
                                </span>
                                <span>{fmt(catPrice * quantities[k])}</span>
                              </div>
                        );
                      })}
                    <div className="flex justify-between text-[10px] font-semibold text-gray-700 pt-1 border-t mt-1">
                      <span>Totaal /mnd</span>
                      <span>{fmt(totalMRR)}</span>
                    </div>
                    <div className="flex justify-between text-[9px] text-gray-400">
                      <span>Jaarlijks</span>
                      <span>{fmt(totalMRR * 12)}</span>
                    </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
